import { createContext, useContext, useEffect } from "react";
import { useRouter } from "next/router";
import { destroyCookie, setCookie } from "nookies";
import { useAuthState } from "react-firebase-hooks/auth";
import { useDocument } from "react-firebase-hooks/firestore";
import {
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  updateEmail,
} from "@firebase/auth";

import { firebaseAuth, getBriefRef, getProfileRef } from "../libs/firebase";

const PUBLIC_PATHS = ["/", "/register", "/reset-password"];
const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const router = useRouter();
  const [user, loading, error] = useAuthState(firebaseAuth);
  const [profile] = useDocument(getProfileRef(user?.uid));
  const [brief] = useDocument(getBriefRef(user?.uid));

  useEffect(() => {
    const isPublic = PUBLIC_PATHS.includes(router.pathname);

    if (!isPublic && !user && !loading) {
      router.push("/");
      console.log("user unauthorized");
      return;
    }

    if (
      user &&
      profile &&
      !profile.exists() &&
      router.pathname !== "/profile"
    ) {
      router.push("/profile");
      return;
    }

    if (isPublic && user && profile && profile?.data()?.phoneNumber) {
      router.push("/dashboard");
      return;
    }
  }, [loading, user, profile, router]);

  useEffect(() => {
    if (user) {
      user.getIdToken().then((token) => {
        setCookie(null, "ctoken", token, { path: "/" });
      });

      window.analytics.identify(user.uid, {
        email: user?.email,
        name: user?.displayName,
        phoneNumber: user?.phoneNumber,
      });

      Cohere.identify(user.uid, {
        email: user.email,
        name: user?.displayName,
        avatar: user?.photoURL,
        phoneNumber: user?.phoneNumber,
      });
    }

    if (!user && !loading) {
      destroyCookie(null, "ctoken");
    }
  }, [loading, user]);

  // Firebase Auth APi

  const userSignOut = async () => {
    return await signOut(firebaseAuth).then(() => {
      console.log("signed out");
    });
  };

  const userEmailSignUp = async (email, password) => {
    const auth = firebaseAuth;
    return await createUserWithEmailAndPassword(auth, email, password);
  };

  const userEmailSignIn = async (email, password) => {
    const auth = firebaseAuth;
    return await signInWithEmailAndPassword(auth, email, password);
  };

  const userGoogleSignIn = async () => {
    const auth = firebaseAuth;
    const provider = new GoogleAuthProvider();

    return await signInWithPopup(auth, provider).catch((error) =>
      console.log("Google SSO closed")
    );
  };

  const userFacebookSignIn = async () => {
    const auth = firebaseAuth;
    const provider = new FacebookAuthProvider();

    return await signInWithPopup(auth, provider).catch((error) =>
      console.log("Facebook SSO closed")
    );
  };

  const userResetPassword = async (email) => {
    const auth = firebaseAuth;
    return await sendPasswordResetEmail(auth, email);
  };

  const userUpdateEmail = async (email) => {
    const auth = firebaseAuth;
    return await updateEmail(auth.currentUser, email);
  };

  if (loading) return null;
  if (user && !profile) return null;
  if (user && !brief) return null;
  if (error) console.log(error.message);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile: profile?.data(),
        brief,
        userSignOut,
        userEmailSignUp,
        userEmailSignIn,
        userGoogleSignIn,
        userFacebookSignIn,
        userResetPassword,
        userUpdateEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
